import React, { useState, useEffect } from "react";
import * as planService from "../services/planService";
import "./TaskEditor.css";

interface ShareDialogProps {
  planId: string;
  planTitle: string;
  onJoined?: (planId: string, title: string) => void;
  onCancel: () => void;
  contextId: string;
  mode?: "share" | "join";
}

const ShareDialog: React.FC<ShareDialogProps> = ({ planId, planTitle, onJoined, onCancel, contextId, mode: initialMode }) => {
  const [mode, setMode] = useState<"share" | "join">(initialMode || "share");
  const [shareCode, setShareCode] = useState("");
  const [loadingCode, setLoadingCode] = useState(false);
  const [copied, setCopied] = useState(false);

  // Share by email
  const [email, setEmail] = useState("");
  const [sharedEmails, setSharedEmails] = useState<string[]>([]);
  const [sharing, setSharing] = useState(false);

  // Join
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [joinResult, setJoinResult] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onCancel]);

  async function handleGetShareCode() {
    setLoadingCode(true);
    try {
      const code = await planService.getShareCode(planId);
      setShareCode(code);
    } catch (err) {
      console.error(err);
    }
    setLoadingCode(false);
  }

  function handleCopy() {
    const url = `${window.location.origin}${window.location.pathname}#/tab?join=${shareCode}`;
    navigator.clipboard.writeText(shareCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function handleShareByEmail() {
    if (!email.trim() || !email.includes("@")) return;
    setSharing(true);
    try {
      await planService.shareWithEmail(planId, email.trim());
      setSharedEmails(prev => [...prev, email.trim()]);
      setEmail("");
    } catch (err) {
      console.error(err);
    }
    setSharing(false);
  }

  async function handleJoin() {
    if (!joinCode.trim()) return;
    setJoining(true);
    setJoinError(null);
    try {
      const result = await planService.joinPlan(joinCode.trim(), contextId);
      if (result.alreadyJoined) {
        setJoinResult(`Already have access to "${result.plan.title}"`);
      } else {
        setJoinResult(`Joined "${result.plan.title}"`);
        onJoined?.(result.plan.id, result.plan.title);
      }
    } catch (err: any) {
      setJoinError(err.message || "Failed to join");
    }
    setJoining(false);
  }

  return (
    <div className="task-editor-overlay" onClick={onCancel}>
      <div className="task-editor" onClick={(e) => e.stopPropagation()}>
        <div className="task-editor-header">
          <h2>{mode === "share" ? "Share Plan" : "Join a Plan"}</h2>
          <button className="task-editor-close" onClick={onCancel}>&#x2715;</button>
        </div>

        <div className="task-editor-body">
          {/* Mode toggle — only show if not forced to a single mode */}
          {!initialMode && (
            <div className="task-editor-field">
              <div className="task-editor-type-pills">
                <button
                  className={`task-editor-pill ${mode === "share" ? "active" : ""}`}
                  onClick={() => setMode("share")}
                >
                  Share
                </button>
                <button
                  className={`task-editor-pill ${mode === "join" ? "active" : ""}`}
                  onClick={() => setMode("join")}
                >
                  Join with Code
                </button>
              </div>
            </div>
          )}

          {mode === "share" && (
            <>
              {/* Share link */}
              <div className="task-editor-field">
                <label>Share Code</label>
                {shareCode ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input type="text" value={shareCode} readOnly style={{ flex: 1, fontFamily: "monospace", fontSize: 16, letterSpacing: 1 }} />
                    <button
                      className="task-editor-btn task-editor-btn-save"
                      style={{ padding: "8px 16px" }}
                      onClick={handleCopy}
                    >
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                ) : (
                  <button
                    className="task-editor-btn task-editor-btn-save"
                    style={{ alignSelf: "flex-start" }}
                    onClick={handleGetShareCode}
                    disabled={loadingCode}
                  >
                    {loadingCode ? "Generating..." : "Generate Share Code"}
                  </button>
                )}
                <span style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                  Anyone with this code can add "{planTitle}" to their plan list.
                </span>
              </div>

              {/* Share by email */}
              <div className="task-editor-field">
                <label>Share by Email</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleShareByEmail(); }}
                    placeholder="colleague@company.com"
                    style={{ flex: 1 }}
                  />
                  <button
                    className="task-editor-btn task-editor-btn-save"
                    style={{ padding: "8px 16px" }}
                    onClick={handleShareByEmail}
                    disabled={sharing || !email.includes("@")}
                  >
                    {sharing ? "Sharing..." : "Share"}
                  </button>
                </div>
                {sharedEmails.length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#888" }}>
                    Shared with: {sharedEmails.join(", ")}
                  </div>
                )}
                <span style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                  They'll see the plan next time they open the app.
                </span>
              </div>
            </>
          )}

          {mode === "join" && (
            <div className="task-editor-field">
              <label>Enter Share Code</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleJoin(); }}
                  placeholder="Paste code here"
                  autoFocus
                  style={{ flex: 1, fontFamily: "monospace", fontSize: 16, letterSpacing: 1 }}
                />
                <button
                  className="task-editor-btn task-editor-btn-save"
                  style={{ padding: "8px 16px" }}
                  onClick={handleJoin}
                  disabled={joining || !joinCode.trim()}
                >
                  {joining ? "Joining..." : "Join"}
                </button>
              </div>
              {joinResult && (
                <div style={{ marginTop: 8, padding: "8px 12px", background: "#e8f5e9", borderRadius: 6, fontSize: 13, color: "#2E7D32" }}>
                  {joinResult}
                </div>
              )}
              {joinError && (
                <div style={{ marginTop: 8, padding: "8px 12px", background: "#fce4ec", borderRadius: 6, fontSize: 13, color: "#c62828" }}>
                  {joinError}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="task-editor-footer">
          <div className="task-editor-footer-left" />
          <div className="task-editor-footer-right">
            <button className="task-editor-btn task-editor-btn-cancel" onClick={onCancel}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareDialog;
